// src/components/ProfessionalPaymentDashboard.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

// =====================================================
// INTERFACES
// =====================================================

interface PaymentSummary {
  totalFacturado: number;
  pendienteCobro: number;
  totalCobrado: number;
  totalTurnosPagados: number;
  turnosPendientesTransferir: number;
  porMetodo: {
    mercadopago: number;
    transferencia: number;
    efectivo: number;
  };
}

interface PendingPayment {
  id: number;
  appointment_date: string;
  appointment_time: string;
  client_name: string;
  client_email: string;
  client_phone?: string;
  price: number;
  metodo_pago_elegido: string;
  pago_verificado_at: string;
  mp_payment_id?: string;
  comprobante_pago_url?: string;
  service_name?: string;
  service_duration?: number;
}

interface Transfer {
  id: number;
  appointment_date: string;
  appointment_time: string;
  client_name: string;
  price: number;
  monto_transferido: number;
  fecha_transferencia: string;
  referencia_transferencia?: string;
  notas_transferencia?: string;
  metodo_pago_elegido: string;
  service_name?: string;
}

// =====================================================
// COMPONENTE PRINCIPAL
// =====================================================

const ProfessionalPaymentDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  
  // Estados para datos
  const [summary, setSummary] = useState<PaymentSummary | null>(null);
  const [pendingPayments, setPendingPayments] = useState<PendingPayment[]>([]);
  const [transferHistory, setTransferHistory] = useState<Transfer[]>([]);
  
  // Estados de UI
  const [activeTab, setActiveTab] = useState<'pending' | 'history'>('pending');
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Modal de solicitud de retiro
  const [showWithdrawalModal, setShowWithdrawalModal] = useState(false);
  const [withdrawalAmount, setWithdrawalAmount] = useState('');
  const [withdrawalMethod, setWithdrawalMethod] = useState('transferencia');
  const [withdrawalNotes, setWithdrawalNotes] = useState('');
  const [submittingWithdrawal, setSubmittingWithdrawal] = useState(false);

  // =====================================================
  // EFECTOS
  // =====================================================

  useEffect(() => {
    const userData = localStorage.getItem('user');
    const loginStatus = localStorage.getItem('isLoggedIn');
    
    if (!userData || loginStatus !== 'true') {
      navigate('/login');
      return;
    }

    try {
      const parsedUser = JSON.parse(userData);
      
      if (!parsedUser.es_profesional) {
        navigate('/dashboard');
        return;
      }
      
      setUser(parsedUser);
      console.log('👤 Usuario verificado:', parsedUser);
      
    } catch (error) {
      console.error('Error parsing user:', error);
      navigate('/login');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (user?.id) {
      loadAllData();
    }
  }, [user?.id]);

  // =====================================================
  // FUNCIONES DE CARGA DE DATOS
  // =====================================================

  const loadAllData = async () => {
    await Promise.all([
      loadSummary(),
      loadPendingPayments(),
      loadTransferHistory()
    ]);
  };

  const loadSummary = async () => {
    setLoadingSummary(true);
    setError(null);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('http://localhost:5000/api/professional/payments/summary', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Error al cargar resumen');
      }

      const data = await response.json();
      console.log('📊 Resumen cargado:', data.summary);
      setSummary(data.summary);
      
    } catch (error) {
      console.error('❌ Error cargando resumen:', error);
      setError('Error al cargar el resumen financiero');
    } finally {
      setLoadingSummary(false);
    }
  };

  const loadPendingPayments = async () => {
    setLoadingPayments(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('http://localhost:5000/api/professional/payments/pending', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Error al cargar pagos pendientes');
      }

      const data = await response.json();
      console.log('💳 Pagos pendientes cargados:', data.payments.length);
      setPendingPayments(data.payments);
      
    } catch (error) {
      console.error('❌ Error cargando pagos pendientes:', error);
    } finally {
      setLoadingPayments(false);
    }
  };

  const loadTransferHistory = async () => {
    setLoadingHistory(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch('http://localhost:5000/api/professional/payments/history', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Error al cargar histórico');
      }

      const data = await response.json();
      console.log('📜 Histórico cargado:', data.transfers.length);
      setTransferHistory(data.transfers);
      
    } catch (error) {
      console.error('❌ Error cargando histórico:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  // =====================================================
  // FUNCIONES DE ACCIONES
  // =====================================================

  const handleRequestWithdrawal = async () => {
    if (!withdrawalAmount || parseFloat(withdrawalAmount) <= 0) {
      alert('Por favor ingresa un monto válido');
      return;
    }

    const amount = parseFloat(withdrawalAmount);
    
    if (summary && amount > summary.pendienteCobro) {
      alert(`El monto no puede ser mayor a tu saldo disponible: $${summary.pendienteCobro}`);
      return;
    }

    setSubmittingWithdrawal(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch('http://localhost:5000/api/professional/payments/request-withdrawal', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          monto: amount,
          metodo_preferido: withdrawalMethod,
          notas: withdrawalNotes
        })
      });

      if (!response.ok) {
        throw new Error('Error al solicitar retiro');
      }

      const data = await response.json();
      console.log('✅ Solicitud de retiro enviada:', data);
      
      alert('✅ Solicitud de retiro enviada correctamente. Te contactaremos pronto.');
      
      // Cerrar modal y resetear
      setShowWithdrawalModal(false);
      setWithdrawalAmount('');
      setWithdrawalNotes('');
      
      // Recargar datos
      loadAllData();
      
    } catch (error) {
      console.error('❌ Error solicitando retiro:', error);
      alert('Error al solicitar el retiro. Intenta nuevamente.');
    } finally {
      setSubmittingWithdrawal(false);
    }
  };

  // =====================================================
  // FUNCIONES DE UTILIDAD
  // =====================================================

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-AR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('es-AR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPaymentMethodBadge = (method: string) => {
    const styles = {
      mercadopago: 'bg-blue-100 text-blue-800',
      transferencia: 'bg-green-100 text-green-800',
      efectivo: 'bg-yellow-100 text-yellow-800'
    };

    const labels = {
      mercadopago: 'Mercado Pago',
      transferencia: 'Transferencia',
      efectivo: 'Efectivo'
    };

    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[method as keyof typeof styles] || 'bg-gray-100 text-gray-800'}`}>
        {labels[method as keyof typeof labels] || method}
      </span>
    );
  };

  // =====================================================
  // RENDER
  // =====================================================

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-green-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Cargando dashboard de pagos...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        
        {/* Header */}
        <div className="mb-8">
          <button
            onClick={() => navigate(-1)}
            className="mb-4 flex items-center text-gray-600 hover:text-gray-900"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Volver
          </button>
          
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">💰 Dashboard de Pagos</h1>
              <p className="text-gray-600 mt-1">Gestiona tus ingresos y solicita retiros</p>
            </div>
            
            <button
              onClick={() => setShowWithdrawalModal(true)}
              disabled={!summary || summary.pendienteCobro <= 0}
              className="bg-green-500 text-white px-6 py-3 rounded-lg font-semibold hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Solicitar Retiro</span>
            </button>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center justify-between">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-red-500 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span className="text-red-800">{error}</span>
            </div>
            <button
              onClick={() => setError(null)}
              className="text-red-500 hover:text-red-700"
            >
              ✕
            </button>
          </div>
        )}

        {/* Resumen Financiero - Cards */}
        {loadingSummary ? (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="bg-white rounded-xl p-6 shadow-sm animate-pulse">
                <div className="h-4 bg-gray-200 rounded mb-3"></div>
                <div className="h-8 bg-gray-200 rounded"></div>
              </div>
            ))}
          </div>
        ) : summary && (
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            {/* Pendiente de Cobro */}
            <div className="bg-white rounded-xl shadow-sm border-2 border-yellow-200 p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-600">Pendiente de Cobro</span>
                <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                  <span className="text-xl">⏳</span>
                </div>
              </div>
              <p className="text-3xl font-bold text-yellow-600">{formatCurrency(summary.pendienteCobro)}</p>
              <p className="text-xs text-gray-500 mt-1">{summary.turnosPendientesTransferir} turnos</p>
            </div>

            {/* Total Cobrado */}
            <div className="bg-white rounded-xl shadow-sm border-2 border-green-200 p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-600">Total Cobrado</span>
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <span className="text-xl">✅</span>
                </div>
              </div>
              <p className="text-3xl font-bold text-green-600">{formatCurrency(summary.totalCobrado)}</p>
              <p className="text-xs text-gray-500 mt-1">Transferencias completadas</p>
            </div>

            {/* Total Facturado */}
            <div className="bg-white rounded-xl shadow-sm border-2 border-blue-200 p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-600">Total Facturado</span>
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <span className="text-xl">💼</span>
                </div>
              </div>
              <p className="text-3xl font-bold text-blue-600">{formatCurrency(summary.totalFacturado)}</p>
              <p className="text-xs text-gray-500 mt-1">{summary.totalTurnosPagados} turnos pagados</p>
            </div>

            {/* Por Método */}
            <div className="bg-white rounded-xl shadow-sm border-2 border-purple-200 p-6">
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium text-gray-600">Por Método</span>
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <span className="text-xl">💳</span>
                </div>
              </div>
              <div className="space-y-1 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">MP:</span>
                  <span className="font-semibold">{formatCurrency(summary.porMetodo.mercadopago)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Transf:</span>
                  <span className="font-semibold">{formatCurrency(summary.porMetodo.transferencia)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Efec:</span>
                  <span className="font-semibold">{formatCurrency(summary.porMetodo.efectivo)}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Tabs */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="border-b border-gray-200">
            <div className="flex">
              <button
                onClick={() => setActiveTab('pending')}
                className={`flex-1 px-6 py-4 text-center font-medium transition-colors ${
                  activeTab === 'pending'
                    ? 'bg-yellow-50 text-yellow-700 border-b-2 border-yellow-500'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center justify-center space-x-2">
                  <span>💳</span>
                  <span>Pagos Pendientes</span>
                  {pendingPayments.length > 0 && (
                    <span className="bg-yellow-500 text-white text-xs px-2 py-1 rounded-full">
                      {pendingPayments.length}
                    </span>
                  )}
                </div>
              </button>
              
              <button
                onClick={() => setActiveTab('history')}
                className={`flex-1 px-6 py-4 text-center font-medium transition-colors ${
                  activeTab === 'history'
                    ? 'bg-green-50 text-green-700 border-b-2 border-green-500'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center justify-center space-x-2">
                  <span>📜</span>
                  <span>Histórico de Cobros</span>
                </div>
              </button>
            </div>
          </div>

          {/* Tab Content */}
          <div className="p-6">
            {activeTab === 'pending' && (
              <div>
                {loadingPayments ? (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-500 mx-auto"></div>
                    <p className="text-gray-600 mt-4">Cargando pagos pendientes...</p>
                  </div>
                ) : pendingPayments.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-3xl">💳</span>
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No hay pagos pendientes</h3>
                    <p className="text-gray-600">Todos tus pagos han sido transferidos</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {pendingPayments.map(payment => (
                      <div
                        key={payment.id}
                        className="border border-yellow-200 rounded-lg p-4 hover:shadow-sm transition-shadow bg-yellow-50"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <h4 className="font-semibold text-gray-900">{payment.client_name}</h4>
                              {getPaymentMethodBadge(payment.metodo_pago_elegido)}
                            </div>
                            
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600">
                              <div>
                                <span className="font-medium">📅 Fecha:</span>
                                <p>{formatDate(payment.appointment_date)}</p>
                              </div>
                              <div>
                                <span className="font-medium">🕐 Hora:</span>
                                <p>{payment.appointment_time}</p>
                              </div>
                              <div>
                                <span className="font-medium">💼 Servicio:</span>
                                <p>{payment.service_name || 'No especificado'}</p>
                              </div>
                              <div>
                                <span className="font-medium">✅ Verificado:</span>
                                <p>{formatDateTime(payment.pago_verificado_at)}</p>
                              </div>
                            </div>

                            {payment.comprobante_pago_url && (
                              <div className="mt-2">
                                <a
                                  href={payment.comprobante_pago_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:text-blue-800 text-sm"
                                >
                                  📎 Ver comprobante
                                </a>
                              </div>
                            )}
                          </div>

                          <div className="ml-4 text-right">
                            <p className="text-2xl font-bold text-yellow-600">
                              {formatCurrency(payment.price)}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">Pendiente</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'history' && (
              <div>
                {loadingHistory ? (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto"></div>
                    <p className="text-gray-600 mt-4">Cargando histórico...</p>
                  </div>
                ) : transferHistory.length === 0 ? (
                  <div className="text-center py-12">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <span className="text-3xl">📜</span>
                    </div>
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Sin transferencias aún</h3>
                    <p className="text-gray-600">El histórico aparecerá cuando se realicen las transferencias</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {transferHistory.map(transfer => (
                      <div
                        key={transfer.id}
                        className="border border-green-200 rounded-lg p-4 hover:shadow-sm transition-shadow bg-green-50"
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <h4 className="font-semibold text-gray-900">{transfer.client_name}</h4>
                              {getPaymentMethodBadge(transfer.metodo_pago_elegido)}
                              <span className="px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs font-medium">
                                ✅ Transferido
                              </span>
                            </div>
                            
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-gray-600">
                              <div>
                                <span className="font-medium">📅 Turno:</span>
                                <p>{formatDate(transfer.appointment_date)}</p>
                              </div>
                              <div>
                                <span className="font-medium">💸 Transferido:</span>
                                <p>{formatDateTime(transfer.fecha_transferencia)}</p>
                              </div>
                              <div>
                                <span className="font-medium">💼 Servicio:</span>
                                <p>{transfer.service_name || 'No especificado'}</p>
                              </div>
                              {transfer.referencia_transferencia && (
                                <div>
                                  <span className="font-medium">🔢 Referencia:</span>
                                  <p className="font-mono text-xs">{transfer.referencia_transferencia}</p>
                                </div>
                              )}
                            </div>

                            {transfer.notas_transferencia && (
                              <div className="mt-2 text-sm text-gray-600">
                                <span className="font-medium">📝 Notas:</span> {transfer.notas_transferencia}
                              </div>
                            )}
                          </div>

                          <div className="ml-4 text-right">
                            <p className="text-2xl font-bold text-green-600">
                              {formatCurrency(transfer.monto_transferido)}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">Cobrado</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Modal de Solicitud de Retiro */}
        {showWithdrawalModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-md w-full p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-xl font-bold text-gray-900">Solicitar Retiro</h3>
                <button
                  onClick={() => setShowWithdrawalModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-4">
                {/* Saldo disponible */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-sm text-gray-600 mb-1">Saldo disponible:</p>
                  <p className="text-2xl font-bold text-yellow-600">
                    {summary ? formatCurrency(summary.pendienteCobro) : '$0'}
                  </p>
                </div>

                {/* Monto a retirar */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Monto a retirar
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-3 text-gray-500">$</span>
                    <input
                      type="number"
                      value={withdrawalAmount}
                      onChange={(e) => setWithdrawalAmount(e.target.value)}
                      placeholder="0"
                      className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                    />
                  </div>
                  {summary && parseFloat(withdrawalAmount) > summary.pendienteCobro && (
                    <p className="text-red-600 text-sm mt-1">
                      El monto no puede ser mayor al saldo disponible
                    </p>
                  )}
                </div>

                {/* Método preferido */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Método preferido
                  </label>
                  <select
                    value={withdrawalMethod}
                    onChange={(e) => setWithdrawalMethod(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  >
                    <option value="transferencia">Transferencia Bancaria</option>
                    <option value="mercadopago">Mercado Pago</option>
                    <option value="efectivo">Efectivo</option>
                  </select>
                </div>

                {/* Notas adicionales */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Notas adicionales (opcional)
                  </label>
                  <textarea
                    value={withdrawalNotes}
                    onChange={(e) => setWithdrawalNotes(e.target.value)}
                    placeholder="Ej: CBU/CVU, alias, horarios disponibles para coordinar..."
                    rows={3}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent resize-none"
                  />
                </div>

                {/* Botones */}
                <div className="flex space-x-3 pt-4">
                  <button
                    onClick={() => setShowWithdrawalModal(false)}
                    className="flex-1 px-4 py-3 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handleRequestWithdrawal}
                    disabled={!withdrawalAmount || parseFloat(withdrawalAmount) <= 0 || submittingWithdrawal}
                    className="flex-1 px-4 py-3 bg-green-500 text-white rounded-lg font-medium hover:bg-green-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                  >
                    {submittingWithdrawal ? (
                      <>
                        <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>Enviando...</span>
                      </>
                    ) : (
                      <>
                        <span>Solicitar Retiro</span>
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                        </svg>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default ProfessionalPaymentDashboard;